import { useState } from 'react';

import SortIcon from '~shared/assets/icons/arrow-switch.svg?react';
import type { DataTableSorting, SortDirection } from '~shared/ui/data-table';

import { Button } from '@breezeventures/ui-kit/button';
import { Modal } from '@breezeventures/ui-kit/modal';
import { RadioGroup } from '@breezeventures/ui-kit/radio-group';
import { SegmentedControl } from '@breezeventures/ui-kit/segmented-control';
import { Typography } from '@breezeventures/ui-kit/typography';

type SortOption = {
  id: string;
  label: string;
};

type Props = {
  options: SortOption[];
  value: DataTableSorting;
  onApply: (value: DataTableSorting) => void;
};

export function TableSortButton({ options, value, onApply }: Props) {
  const [opened, setOpened] = useState(false);
  const [field, setField] = useState(value.id);
  const [direction, setDirection] = useState<SortDirection>(value.direction);

  const open = () => {
    setField(value.id);
    setDirection(value.direction);
    setOpened(true);
  };

  const apply = () => {
    onApply({ id: field, direction });
    setOpened(false);
  };

  return (
    <>
      <Button type="button" size="s" variant="secondary" onClick={open}>
        <SortIcon className="size-4" />
        Sort
      </Button>

      <Modal opened={opened} onClose={() => setOpened(false)}>
        <Typography.Text
          size="m"
          weight="medium"
          tag="p"
          className="mb-4 text-center"
        >
          Sort by
        </Typography.Text>

        <div className="flex flex-col gap-6">
          <SegmentedControl
            width="full"
            size="m"
            value={direction}
            onChange={(next) => {
              if (next) setDirection(next as SortDirection);
            }}
          >
            <SegmentedControl.Item value="asc">Ascending</SegmentedControl.Item>
            <SegmentedControl.Item value="desc">
              Descending
            </SegmentedControl.Item>
          </SegmentedControl>

          <RadioGroup.Root
            value={field}
            onChange={setField}
            className={`
              divide-y divide-separator overflow-hidden rounded-2xl border
              border-separator
            `}
          >
            {options.map((option) => (
              <RadioGroup.Item
                key={option.id}
                value={option.id}
                className="p-4"
              >
                {option.label}
              </RadioGroup.Item>
            ))}
          </RadioGroup.Root>
        </div>

        <div className="mt-6">
          <Modal.FooterActions>
            <Button variant="primary" size="l" width="full" onClick={apply}>
              Apply
            </Button>
          </Modal.FooterActions>
        </div>
      </Modal>
    </>
  );
}
